import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  ISourceEntityTypes,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, toINamedDocuments } from '@principle-theorem/principle-core';
import { type IReferralSourceConfiguration } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  asyncForEach,
  multiMap,
  multiSortBy$,
  nameSorter,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import Fuse from 'fuse.js';
import { compact, first, sortBy } from 'lodash';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { ReferralSourceToXSLX } from './referral-sources-to-xlsx';
import { XSLXToReferralSources } from './xlsx-to-referral-sources';

export interface IBaseMigrationReferralSource {
  id: string;
  name: string;
}

export const REFERRAL_SOURCE_CUSTOM_MAPPING_TYPE = 'referralSource';

export const REFERRAL_SOURCE_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Referral Sources',
    description: `Used for mapping Contacts to the correct Referral Sources in Principle.`,
    type: REFERRAL_SOURCE_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Referral Source',
    destinationIdentifier: 'Principle Referral Source',
  },
});

export abstract class BaseReferralSourceMappingHandler<
  ReferralSource extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<ReferralSource>,
> extends BaseCustomMappingHandler<IReferralSourceConfiguration> {
  customMapping = REFERRAL_SOURCE_MAPPING;
  abstract translateFn: (
    record: ReferralSource['data']
  ) => IBaseMigrationReferralSource;
  abstract sourceEntity: SourceEntity;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const referralSource = this.translateFn(record);
        return {
          label: referralSource.name,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IReferralSourceConfiguration>[]> {
    return snapshot(
      Brand.referralSources$(migration.configuration.brand).pipe(
        toINamedDocuments(),
        multiSortBy$(nameSorter()),
        multiMap((referralSource) => ({
          name: referralSource.name,
          ref: referralSource.ref,
        })),
        multiSortBy$(nameSorter())
      )
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const records = await this.getRecords(translationMap);

    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = sourceOptions.filter(
      (option) => !mappedItemCodes.includes(option.value)
    );

    const recordsForUpload = unmappedItemCodes.map((item) => {
      const fuse = new Fuse(destinationOptions, {
        keys: ['name'],
        includeScore: true,
        threshold: 0.3,
      });
      const matches = fuse.search(item.label);
      const match = first(matches)?.item;

      return match
        ? {
            destinationIdentifier: match.ref,
            destinationValue: { value: match.name },
            sourceIdentifier: item.value,
            sourceLabel: item.label,
          }
        : undefined;
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, referralSources, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      referralSources,
      translators
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, referralSources, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      referralSources,
      translators
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToReferralSources()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const referralSourceConfigurations =
      await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const mapTo = item.mapTo;
      const id = item.sourceId;
      const label = item.sourceName;
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.value === id
      )?.value;
      if (!value || !item.mapTo) {
        return;
      }

      const referralSourceConfiguration = referralSourceConfigurations.find(
        (configuration) => configuration.name === mapTo
      );

      if (!referralSourceConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find referral source for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationIdentifier: referralSourceConfiguration.ref,
        sourceIdentifier: value,
        sourceLabel: label,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    referralSources: ICustomMappingSourceOption[];
    translators: ReferralSourceToXSLX;
  }> {
    const fileName = this.getFileName();
    const referralSources = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translators = new ReferralSourceToXSLX(
      await snapshot(Brand.referralSources$(migration.configuration.brand)),
      await this.getRecords(translationMap)
    );
    return { fileName, referralSources, translators };
  }
}
